/* _letterSelector.js — Batch T follow-up · workflow integration · Batch B (Feb 2026).
 *
 * Shared decision tree for the `accepted_deposit_state` letter-variant
 * selector used by both `LetterPreview.js` (single letter) and
 * `SendBundle.js` (letter list, currently 1). Extracted so:
 *   · Both files share ONE source of truth (previously duplicated 30 lines
 *     with subtle diffs — a footgun waiting to happen).
 *   · The decision tree is Jest-testable in isolation.
 *
 * Contract: given `capturedData` from a Workflow Runner Accepted-status run,
 * return one of the 3 canonical letter keys:
 *   · 'accepted_no_deposit_required' — no children need a deposit.
 *   · 'accepted_awaiting_deposit'    — at least one deposit is due, not
 *                                       yet captured.
 *   · 'accepted_deposit_received'    — every required deposit is captured.
 *
 * Priority chain:
 *   1. `per_child_deposit_pick` roll-up (multi-variant flow, written by
 *      PerChildDepositCapture) — most accurate.
 *   2. Parent appraisal's Deposit Engine value (single-quote flow,
 *      persisted by _recalculate_deposit in _component_sync.py).
 *   3. Legacy pick_accepted_variants / accepted_variants_pick keys
 *      (older workflow definitions).
 *   4. Legacy `deposit_amount` on the parent appraisal (pre-Batch-T docs).
 *
 * Batch A already unified the read-sites; Batch B locks the SELECTOR
 * decision tree behind Jest coverage so a future refactor can't silently
 * regress the wrong-letter-goes-out bug.
 */

/**
 * Roll up the deposit state from any of the sources listed above.
 * Returns `{ anyRequired, allCaptured }` for the selector to decide.
 */
export function rollupDepositState(capturedData) {
  // 1. Per-child roll-up (multi-variant flow) — richest signal, run-local.
  const perChild = capturedData?.per_child_deposit_pick;
  if (perChild && typeof perChild === 'object') {
    return {
      anyRequired: !!perChild.deposit_any_required,
      allCaptured: !!perChild.deposit_all_captured,
    };
  }

  // 1.5 Persisted appraisal deposit state — Pre-UAT v10.12 (Feb 2026 · Audit
  // Pass 3 F-P3-P0-001). After the v10.10 mid-status hop, T20 (Client Notified)
  // starts a FRESH run whose captured_data has no `per_child_deposit_pick`
  // or `deposit_capture_result` (both are run-local, written to T10). The
  // persisted truth (`per_child_deposits`, `deposit_captured_at`, `deposit`)
  // IS on the appraisal doc — written by PerChildDepositCapture/DepositCapture
  // at T10. Read it here BEFORE falling through to the legacy amount check,
  // otherwise a paid client gets an "awaiting deposit" letter. `appraisal` is
  // the live hydrated snapshot (WorkflowRunnerPage.js:191-208); `appraisal_record`
  // is the start-of-run seed (workflow_runs.py:285) — check both.
  const persisted = capturedData?.appraisal || capturedData?.appraisal_record;
  if (persisted && typeof persisted === 'object') {
    const perChildPersisted = persisted.per_child_deposits;
    if (perChildPersisted && typeof perChildPersisted === 'object') {
      const rows = Object.values(perChildPersisted);
      // A row means "required" if it's NOT `not_required`; captured if
      // it has a stamped_at OR the awaiting flag is falsy AND a stamped_at exists.
      const rowsRequired = rows.filter(d => d && !d.not_required);
      if (rowsRequired.length > 0) {
        const allStamped = rowsRequired.every(d => !!(d.stamped_at));
        return { anyRequired: true, allCaptured: allStamped };
      }
      // rows all marked not_required → no deposit needed
      if (rows.length > 0) return { anyRequired: false, allCaptured: true };
    }
    // Single-quote flow persistence — `deposit.stamped_at` + `deposit_captured_at`.
    const persistedDeposit = persisted.deposit;
    const persistedCapturedAt = persisted.deposit_captured_at;
    const persistedRequired = persisted.deposit_required;
    const persistedEngineAmount = Number(persisted.deposit_engine_amount ?? 0);
    if (persistedRequired === true || persistedDeposit || persistedCapturedAt) {
      // Pre-UAT v10.13 fix (Feb 2026 · testing agent iteration 318) — a
      // PARTIAL deposit persists as `{amount:100, stamped_at:<iso>,
      // partial:true}` with `deposit_captured_at: null`. The prior check
      // (v10.12) treated any `stamped_at` as fully captured → wrong
      // letter ("Thank you for your deposit" to a client who paid $100
      // of $500). Correct rule: fully captured ONLY when
      //   (a) `deposit_captured_at` is stamped (backend engine only sets
      //       this once engine ≤ sum(captured)), OR
      //   (b) `deposit` object exists with `partial !== true` AND its
      //       amount is ≥ the engine-required amount.
      // A stamped_at alone is NOT sufficient — partial captures also
      // stamp_at on the row.
      let captured = false;
      if (persistedCapturedAt) {
        captured = true;
      } else if (persistedDeposit && persistedDeposit.partial !== true) {
        const paid = Number(persistedDeposit.amount ?? 0);
        // If engine is unknown (0), fall back to "any stamped amount > 0"
        // to preserve legacy behaviour for older records where
        // deposit_engine_amount was never persisted.
        if (persistedEngineAmount > 0) {
          captured = paid >= persistedEngineAmount;
        } else {
          captured = paid > 0 && !!persistedDeposit.stamped_at;
        }
      }
      return { anyRequired: !!persistedRequired || !!persistedDeposit || !!persistedCapturedAt, allCaptured: captured };
    }
  }

  // 2-4. Legacy single-quote / older-runs fallback chain.
  const requiredTotal = Number(
    capturedData?.appraisal?.deposit_engine_amount
    ?? capturedData?.appraisal_record?.deposit_engine_amount
    ?? capturedData?.deposit_required_total
    ?? capturedData?.pick_accepted_variants?.deposit_required_total
    ?? capturedData?.accepted_variants_pick?.deposit_required_total
    ?? capturedData?.appraisal?.deposit_amount
    ?? 0
  );
  if (requiredTotal <= 0) {
    return { anyRequired: false, allCaptured: true };
  }
  const dep = capturedData?.deposit_capture_result?.deposit
    || capturedData?.deposit_capture_result;
  const captured = !!(dep && (dep.stamped_at || (Number(dep.amount) || 0) > 0));
  return { anyRequired: true, allCaptured: captured };
}

/**
 * Given the rolled-up state, return the correct letter key.
 * Kept as a separate function so callers can also test the roll-up
 * without the mapping step.
 */
export function letterKeyForDepositState({ anyRequired, allCaptured }) {
  if (!anyRequired) return 'accepted_no_deposit_required';
  return allCaptured ? 'accepted_deposit_received' : 'accepted_awaiting_deposit';
}

/**
 * One-shot selector: given `selector` name + `capturedData`, return the
 * letter key OR `notFound` (default null) if we don't recognise the
 * selector.
 */
export function selectAcceptedLetterKey(selector, capturedData, notFound = null) {
  if (selector !== 'accepted_deposit_state') return notFound;
  return letterKeyForDepositState(rollupDepositState(capturedData));
}
